import { useState, useEffect } from "react";
import { X, ArrowRight, Sparkles } from "lucide-react";

/**
 * QuoteNudge — Friendly toast popup that slides in from bottom-left after a delay,
 * inviting the user to get a free quote. Dismissible (remembered in sessionStorage).
 */
export const QuoteNudge = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("brs-nudge-dismissed") === "1") return;

    const t = setTimeout(() => setShow(true), 8000); // appear after 8s
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      sessionStorage.setItem("brs-nudge-dismissed", "1");
    } catch {
      /* noop */
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="quote-nudge"
      className="fixed bottom-4 left-4 z-40 max-w-[320px] md:max-w-[360px] slide-up"
      role="dialog"
      aria-label="Kostenloses Angebot"
    >
      <div className="relative rounded-2xl bg-white border-2 border-black p-4 md:p-5 shadow-[6px_6px_0_rgba(0,0,0,1)]">
        {/* Close */}
        <button
          type="button"
          onClick={dismiss}
          data-testid="quote-nudge-close"
          aria-label="Schließen"
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-black/[0.05] hover:bg-black/10 flex items-center justify-center text-black/55 hover:text-black transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="flex items-start gap-3">
          {/* Icon with green glow */}
          <div className="relative shrink-0">
            <span className="absolute inset-0 rounded-2xl bg-[#25D366] now-pulse" />
            <div className="relative w-11 h-11 rounded-2xl bg-[#25D366] text-black flex items-center justify-center border-2 border-black">
              <Sparkles className="w-5 h-5" />
            </div>
          </div>

          <div className="min-w-0 pr-4">
            <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-black/55 mb-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#25D366] mr-1.5 align-middle blink-dot" />
              Heute frei
            </div>
            <div className="font-display text-base md:text-lg font-semibold tracking-tight leading-tight text-black">
              Hey! Hol dir jetzt dein kostenloses Angebot.
            </div>
            <p className="mt-1.5 text-xs text-black/60 leading-relaxed">
              60 Sekunden ausfüllen → wir melden uns mit Festpreis zurück.
            </p>

            <a
              href="#angebot"
              onClick={() => setShow(false)}
              data-testid="quote-nudge-cta"
              className="group mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-black text-white text-xs font-semibold hover:bg-[#25D366] hover:text-black transition-colors"
            >
              Zum Formular
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
